import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { RefreshCw, Database, TrendingUp, Zap } from 'lucide-react';
interface SlideStrategyProps {
  onContinue: () => void;
}
export function SlideStrategy({
  onContinue
}: SlideStrategyProps) {
  const [stage, setStage] = useState(0);
  const pillars = [{
    icon: <Database className="w-5 h-5" data-id="element-477" />,
    title: 'Leverage Existing Assets',
    desc: 'No new content creation required. We repurpose your approved materials into interactive Snaks™ — ready in days.',
    tint: 'from-violet-100/70 to-violet-50/30',
    iconText: 'text-[#6C2BD9]'
  }, {
    icon: <Zap className="w-5 h-5" data-id="element-478" />,
    title: 'Rapid Deployment',
    desc: 'Centralized creation with localized rollout. Campaigns launch in weeks, not months — across countries and channels.',
    tint: 'from-purple-100/70 to-purple-50/30',
    iconText: 'text-purple-500'
  }, {
    icon: <TrendingUp className="w-5 h-5" data-id="element-479" />,
    title: 'Behavioral Data Capture',
    desc: 'Move beyond vanity metrics. Polls, quizzes and interactions reveal what truly drives HCP decisions and knowledge gaps.',
    tint: 'from-rose-100/70 to-rose-50/30',
    iconText: 'text-rose-500'
  }, {
    icon: <RefreshCw className="w-5 h-5" data-id="element-480" />,
    title: 'Continuous Optimization',
    desc: 'A closed-loop system where every interaction informs and sharpens the next campaign — automatically.',
    tint: 'from-teal-100/70 to-teal-50/30',
    iconText: 'text-teal-600'
  }];
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 200);
    const t2 = setTimeout(() => setStage(2), 500);
    const timers = pillars.map((_, idx) => setTimeout(() => setStage(3 + idx), 800 + idx * 220));
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      timers.forEach(clearTimeout);
    };
  }, []);
  return <div className="h-full w-full flex flex-col items-center px-8 py-12 md:px-16 md:py-16 max-w-7xl mx-auto overflow-y-auto" data-id="element-481">
      {/* Heading */}
      <motion.h2 initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 16
    }} transition={{
      duration: 0.5
    }} className="font-serif text-5xl md:text-6xl lg:text-7xl text-slate-900 leading-tight text-center" data-id="element-482">
        The Operating Model
      </motion.h2>

      {/* Subtitle */}
      <motion.p initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: stage >= 2 ? 1 : 0,
      y: stage >= 2 ? 0 : 10
    }} transition={{
      duration: 0.5
    }} className="mt-5 text-lg md:text-xl text-slate-500 text-center max-w-2xl" data-id="element-483">
        Four pillars that turn approved brand content into continuous,
        measurable engagement.
      </motion.p>

      {/* Pillars grid */}
      <div className="mt-12 md:mt-16 w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6" data-id="element-484">
        {pillars.map((pillar, idx) => <motion.div key={idx} initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: stage >= 3 + idx ? 1 : 0,
        y: stage >= 3 + idx ? 0 : 24
      }} transition={{
        duration: 0.5
      }} className={`bg-gradient-to-br ${pillar.tint} border border-[#6C2BD9]/15 rounded-3xl p-6 flex flex-col`} data-id="element-485">
            <div className={`w-11 h-11 rounded-xl bg-white shadow-sm flex items-center justify-center ${pillar.iconText} mb-5`} data-id="element-486">
              {pillar.icon}
            </div>
            <h3 className="font-sans font-bold text-lg md:text-xl text-slate-900 mb-3 leading-snug" data-id="element-487">
              {pillar.title}
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed" data-id="element-488">
              {pillar.desc}
            </p>
          </motion.div>)}
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 6 ? 1 : 0
    }} transition={{
      duration: 0.5
    }} className="mt-10 md:mt-12 flex justify-end w-full" data-id="element-489">
        <Button onClick={onContinue} data-id="element-490">What Snakker Does</Button>
      </motion.div>
    </div>;
}